import axios, { type AxiosResponse, type AxiosError } from 'axios';
import type { 
  QuizAnswers, 
  UserInfo, 
  QuizSubmissionData, 
  QuizSubmissionResponse, 
  QuizStatusResponse, 
  QuizAnswersResponse, 
  ApiError,
  ReportGenerationRequest,
  ReportGenerationResponse,
  ReportResponse,
  ReportRegenerationRequest,
  ReportRegenerationResponse
} from '../types/api.types';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:5001/scoutout-mang-test-e17bf/asia-southeast1/readiness_api';

// Log API configuration
console.log('🔧 API Configuration:', {
  VITE_API_URL: import.meta.env.VITE_API_URL,
  API_BASE_URL: API_BASE_URL,
  NODE_ENV: import.meta.env.NODE_ENV
});

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error: AxiosError) => {
    console.error('❌ API Response Error:', error.response?.status, error.message);
    return Promise.reject(error);
  }
);

// Types are now imported from ../types/api.types

// API Service Class
class ApiService {
  /**
   * Submit quiz answers to the backend
   */
  async submitQuiz(data: QuizSubmissionData): Promise<QuizSubmissionResponse> {
    try {
      const response: AxiosResponse<QuizSubmissionResponse> = await apiClient.post(
        '/api/quiz/submit',
        data
      );
      return response.data;
    } catch (error) {
      this.handleError(error, 'Failed to submit quiz');
      throw error;
    }
  }

  /**
   * Check the status of a quiz submission
   */
  async getQuizStatus(documentId: string): Promise<QuizStatusResponse> {
    try {
      const response: AxiosResponse<QuizStatusResponse> = await apiClient.get(
        `/api/quiz/status/${documentId}`
      );
      return response.data;
    } catch (error) {
      this.handleError(error, 'Failed to get quiz status');
      throw error;
    }
  }

  /**
   * Retrieve quiz answers (for admin/debugging)
   */
  async getQuizAnswers(documentId: string): Promise<QuizAnswersResponse> {
    try {
      const response: AxiosResponse<QuizAnswersResponse> = await apiClient.get(
        `/api/quiz/answers/${documentId}`
      );
      return response.data;
    } catch (error) {
      this.handleError(error, 'Failed to get quiz answers');
      throw error;
    }
  }

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<{ success: boolean; message: string; timestamp: string }> {
    try {
      const response = await apiClient.get('/health');
      return response.data;
    } catch (error) {
      this.handleError(error, 'Health check failed');
      throw error;
    }
  }

  /**
   * Get API information
   */
  async getApiInfo(): Promise<{ success: boolean; message: string; version: string; endpoints: any }> {
    try {
      const response = await apiClient.get('/');
      return response.data;
    } catch (error) {
      this.handleError(error, 'Failed to get API info');
      throw error;
    }
  }

  /**
   * Handle API errors consistently
   */
  private handleError(error: any, defaultMessage: string): void {
    if (axios.isAxiosError(error)) {
      const apiError = error.response?.data as ApiError;
      if (apiError) {
        console.error(`❌ API Error: ${apiError.error}`, apiError.details);
      } else {
        console.error(`❌ HTTP Error: ${error.response?.status} ${error.message}`);
      }
    } else {
      console.error(`❌ ${defaultMessage}:`, error);
    }
  }

  /**
   * Generate a unique session ID
   */
  generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Prepare quiz data for submission
   */
  prepareQuizData(answers: QuizAnswers, userInfo?: UserInfo, sessionId?: string): QuizSubmissionData {
    return {
      answers,
      userInfo: userInfo || {},
      sessionId: sessionId || this.generateSessionId(),
    };
  }

  // Report API Methods

  /**
   * Generate AI Readiness Report
   */
  async generateReport(data: ReportGenerationRequest): Promise<ReportGenerationResponse> {
    try {
      const response: AxiosResponse<ReportGenerationResponse> = await apiClient.post(
        '/api/report/generate',
        data
      );
      return response.data;
    } catch (error) {
      this.handleError(error, 'Failed to generate report');
      throw error;
    }
  }

  /**
   * Retrieve generated report by report ID
   */
  async getReport(reportId: string): Promise<ReportResponse> {
    try {
      const response: AxiosResponse<ReportResponse> = await apiClient.get(
        `/api/report/${reportId}`
      );
      return response.data;
    } catch (error) {
      this.handleError(error, 'Failed to get report');
      throw error;
    }
  }

  /**
   * Get report by document ID (gets the most recent report for a quiz submission)
   */
  async getReportByDocument(documentId: string): Promise<ReportResponse> {
    try {
      const response: AxiosResponse<ReportResponse> = await apiClient.get(
        `/api/report/by-document/${documentId}`
      );
      return response.data;
    } catch (error) {
      this.handleError(error, 'Failed to get report by document');
      throw error;
    }
  }

  /**
   * Regenerate report with optional custom prompt
   */
  async regenerateReport(data: ReportRegenerationRequest): Promise<ReportRegenerationResponse> {
    try {
      const response: AxiosResponse<ReportRegenerationResponse> = await apiClient.post(
        '/api/report/regenerate',
        data
      );
      return response.data;
    } catch (error) {
      this.handleError(error, 'Failed to regenerate report');
      throw error;
    }
  }
}

// Export singleton instance
export const apiService = new ApiService();

// Export the class for testing
export default ApiService;
